import React, { memo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import EndorsementsModal from 'transfix-form/forms/CarrierFleetInformation/components/EndorsementsModal/EndorsementsModal'
import EquipmentTypesModal from 'transfix-form/forms/CarrierFleetInformation/components/EquipmentTypesModal/EquipmentTypesModal'
import FreightCapabilityModal from 'transfix-form/forms/CarrierFleetInformation/components/FreightCapabilityModal/FreightCapabilityModal'
import {
  getEquipmentTypesDefaultValues,
  getEquipmentTypesLabels,
  getFreightCapabilitiesDefaultValues,
  getFreightCapabilitiesLabels,
  getEndorsementsDefaultValues,
  getEndorsementsLabels,
} from 'transfix-form/forms/CarrierFleetInformation/utils'
import Card from 'transfix-ui/components/Card'
import { Grid, makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { ICarrierEquipmentState } from 'types/carrier'
import { useModalState } from 'utils/hooks/useModalState'
import { getEquipment } from 'apps/carrier/utils/actions/equipment'
import { IStore } from 'apps/carrier/utils/reducers'
import SidebarCardRow from '../SidebarCardRow/SidebarCardRow'
import { ICarrierProfileFleetInfoFragment } from './graphql/CarrierProfileFleetInfoFragment'
import {
  useFreightCapabilitySubmitter,
  useEquipmentTypesSubmitter,
  useEndorsementsSubmitter,
} from './hooks'

export interface IFleetInformationCardProps {
  carrierApplicationId: string
  fleetInfo: Partial<ICarrierProfileFleetInfoFragment>
}

interface ISelector {
  equipment: ICarrierEquipmentState
}

const useFleetInformationCardStyles = makeStyles(theme => ({
  rowSection: {
    marginTop: theme.spacing(2),
  },
}))
const FleetInformationCard = memo<IFleetInformationCardProps>(
  ({
    carrierApplicationId,
    fleetInfo: { endorsements, equipmentCapability, freightCapability },
  }) => {
    const classes = useFleetInformationCardStyles()
    const { createToggleHandler, modalState } = useModalState({
      freightCapabilityModal: false,
      equipmentTypesModal: false,
      endorsementsModal: false,
    })

    const {
      endorsementsModal: isEndorsementsModalOpen,
      equipmentTypesModal: isEquipmentTypesModalOpen,
      freightCapabilityModal: isFreightCapabilityModalOpen,
    } = modalState || {}

    const toggleFreightCapabilityModalHandler = createToggleHandler('freightCapabilityModal')
    const toggleEquipmentTypesModalHandler = createToggleHandler('equipmentTypesModal')
    const toggleEndorsementsModalHandler = createToggleHandler('endorsementsModal')

    /**
     * Freight Capability
     */
    const {
      loading: freightCapabilityLoading,
      submitHandler: freightCapabilitySubmitHandler,
    } = useFreightCapabilitySubmitter(carrierApplicationId)
    const freightCapabilityLabel = getFreightCapabilitiesLabels(freightCapability)
    const freightCapabilityDefaultValues = getFreightCapabilitiesDefaultValues(freightCapability)

    /**
     * Equipment Types
     */
    const {
      loading: equipmentTypesLoading,
      submitHandler: equipmentTypesSubmitHandler,
    } = useEquipmentTypesSubmitter(carrierApplicationId)
    const equipmentTypesLabel = getEquipmentTypesLabels(equipmentCapability)
    const { preferenceLabel: equipmentTypesPreferenceLabels, subLabel: equipmentSubLabel } =
      equipmentTypesLabel || {}
    const equipmentTypesDefaultValues = getEquipmentTypesDefaultValues(equipmentCapability)

    /**
     * Endorsements
     */

    const {
      loading: endorsementsLoading,
      submitHandler: endorsementsSubmitHandler,
    } = useEndorsementsSubmitter(carrierApplicationId)
    const endorsementsLabel = getEndorsementsLabels(endorsements)
    const endorsementsDefaultValues = getEndorsementsDefaultValues(endorsements)

    /**
     * Fetch FMCSA Data
     */

    const dispatch = useDispatch()
    const { equipment } = useSelector<IStore, ISelector>(store => ({
      equipment: store.carrierEquipment,
    }))

    const { equipment: equipmentArray } = equipment ?? {}

    const fmcsaData = equipmentArray?.filter(e => e.key === 'power_units' || e.key === 'trailers')

    useEffect(() => {
      if (carrierApplicationId) {
        dispatch(getEquipment(carrierApplicationId))
      }
    }, [carrierApplicationId, dispatch])

    return (
      <Card>
        <Typography variant='h3'>Fleet Information</Typography>
        <Grid container direction='column'>
          <SidebarCardRow
            title='Freight capabilities'
            value={freightCapabilityLabel}
            onEdit={toggleFreightCapabilityModalHandler}
          />
          <SidebarCardRow
            title='Equipment types'
            value={
              equipmentTypesLabel ? (
                <>
                  <Typography component='div' variant='body2'>
                    {equipmentTypesPreferenceLabels}
                  </Typography>
                  <Typography component='div' variant='body2' color='textSecondary'>
                    {equipmentSubLabel}
                  </Typography>
                </>
              ) : null
            }
            onEdit={toggleEquipmentTypesModalHandler}
          />
          <SidebarCardRow
            title='Endorsements'
            value={endorsementsLabel}
            onEdit={toggleEndorsementsModalHandler}
          />
        </Grid>
        <Grid className={classes.rowSection}>
          <Typography variant='subtitle2'>FMCSA Data</Typography>
          {fmcsaData?.map(equipment => (
            <Box
              key={equipment.key}
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
              mt={1}>
              <Typography component='div' variant='body2'>
                {equipment.key === 'trailers' ? 'Trailers' : 'Power Units'}
              </Typography>
              <Typography component='div' variant='body2'>
                {equipment.count}
              </Typography>
            </Box>
          ))}
        </Grid>

        {isFreightCapabilityModalOpen && (
          <FreightCapabilityModal
            defaultValues={freightCapabilityDefaultValues}
            loading={freightCapabilityLoading}
            onClose={toggleFreightCapabilityModalHandler}
            onSubmit={freightCapabilitySubmitHandler}
          />
        )}

        {isEquipmentTypesModalOpen && (
          <EquipmentTypesModal
            defaultValues={equipmentTypesDefaultValues}
            loading={equipmentTypesLoading}
            onClose={toggleEquipmentTypesModalHandler}
            onSumbit={equipmentTypesSubmitHandler}
          />
        )}
        {isEndorsementsModalOpen && (
          <EndorsementsModal
            defaultValues={endorsementsDefaultValues}
            loading={endorsementsLoading}
            onClose={toggleEndorsementsModalHandler}
            onSubmit={endorsementsSubmitHandler}
          />
        )}
      </Card>
    )
  }
)

export default FleetInformationCard
